import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import toast, { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import ConsoleTable, { DataListProps } from "../-components/ConsoleTable";
import Pagination from "components/Pagination";
import PageHeader from "../-components/PageHeader";
import { makeIntComma, RouteTypes, shortcut, toastErrorSyle } from "hooks/utils/util";
import { useGetConsoleBillInquiry } from "hooks/console/useGetConsoleBillInquiry";
import { useDeleteConsoleBillInquiry } from "hooks/console/useDeleteConsoleBillInquiry";
import ConfirmDialog from "components/popup/ConfirmDialog";
import ConsoleBillCommentDialog from "../-components/ConsoleBillCommentDialog";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { ReactComponent as ReadingGlasses } from "assets/common/common/readingGlasses.svg";
import Seo from "components/Seo";

const theadStyle = "text-[#222222] text-[16px] text-center";

export default function ConsoleInvoicesEdit({ type }: { type: RouteTypes }) {
    const initialSearchForm = {
        category: type,
        page: 1,
        limit: "10",
        searchtxt: "",
    };
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [searchForm, setSearchForm] = useState(initialSearchForm);
    const allCheckRef = useRef<HTMLInputElement | null>(null);
    const { data, refetch, isLoading } = useGetConsoleBillInquiry(searchForm);
    const { mutate: deleteBillInquiry } = useDeleteConsoleBillInquiry();
    const { 청구서수정및문의 } = shortcut();

    // 사이드바 링크 재 클릭시 상태 초기화
    useEffect(() => {
        if (location.state?.reset) {
            // 초기화 로직 수행
            setSearchForm(initialSearchForm);
        }
    }, [location.state]);

    useEffect(() => {
        const page = searchParams.get("page") ?? "1";
        if (page) {
            setSearchForm(prev => ({ ...prev, page: Number(page) }));
        }
    }, [searchParams]);

    const deleteRow = () => {
        deleteBillInquiry(
            { category: type, list_no: selectedRows.map(Number) },
            {
                onSuccess: () => {
                    toast.success("성공적으로 삭제 되었습니다.");
                    refetch();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    };

    const toggleRow = (id: number) => {
        const allIdxs = data?.data_list.map(item => item.list_no);

        setSelectedRows(prev => {
            if (prev.includes(id)) {
                // 이미 선택된 경우 제거
                const newRows = prev.filter(rowId => rowId !== id);
                if (newRows.length !== allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            } else {
                // 없으면 추가
                const newRows = [...prev, id];
                if (newRows.length === allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            }
        });
    };

    const allCheck = (checked: boolean) => {
        const allIdxs = data?.data_list.map(item => item.list_no);

        if (checked) {
            if (allIdxs) setSelectedRows(allIdxs);
        } else {
            setSelectedRows([]);
        }
    };

    return (
        <>
            <Seo type="console" pageTitle="청구서수정요청" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="고객서비스" title="청구서수정요청" linkTo={청구서수정및문의} />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className="px-[40px] py-[28px] w-full overflow-y-auto">
                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[900px]">
                            <form>
                                <div className="flex items-center gap-2 justify-end">
                                    <Select
                                        value={searchForm.limit}
                                        onValueChange={value => setSearchForm(prev => ({ ...prev, limit: value }))}
                                    >
                                        <SelectTrigger
                                            className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[150px] border border-[#E1E2E4] bg-white h-[50px]`}
                                        >
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="20">20개</SelectItem>
                                            <SelectItem value="10">10개</SelectItem>
                                        </SelectContent>
                                    </Select>

                                    {/* <Select
                                        value={searchForm.search}
                                        onValueChange={value => setSearchForm(prev => ({ ...prev, search: value }))}
                                    >
                                        <SelectTrigger
                                            className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[120px] border border-[#E1E2E4] bg-white h-[50px]`}
                                        >
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="subject">제목</SelectItem>
                                            <SelectItem value="contents">내용</SelectItem>
                                        </SelectContent>
                                    </Select> */}
                                    <div className="flex items-center border border-[#dddddd] rounded-[5px] bg-white overflow-hidden w-[320px] px-[16px]">
                                        <input
                                            type="text"
                                            value={searchForm.searchtxt}
                                            onChange={e => setSearchForm(prev => ({ ...prev, searchtxt: e.target.value }))}
                                            placeholder="검색어를 입력해주세요."
                                            className={`text-[16px] placeholder:text-[#878A93] w-full focus:outline-none py-[12px]`}
                                        />
                                        <ReadingGlasses />
                                    </div>
                                </div>
                            </form>

                            <div className="mt-[8px] mb-[40px]">
                                <div className="bg-[#DCE1EC] flex justify-between px-[30px] py-[20px] rounded-[5px]">
                                    <div className="flex items-center gap-[30px]">
                                        <label htmlFor="all" className="flex items-center gap-x-2">
                                            <input type="checkbox" id="all" onChange={e => allCheck(e.target.checked)} ref={allCheckRef} />
                                            <span>전체선택</span>
                                        </label>
                                        <label htmlFor="" className="text-[#222222] text-[18px]">
                                            선택한 게시글 총 <span className="text-[#4F46E5]">{selectedRows.length}</span>개
                                        </label>
                                    </div>

                                    <ConfirmDialog
                                        title="요청 삭제"
                                        trigger={open => (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (selectedRows.length === 0) {
                                                        toast.error("삭제할 게시글을 선택해주세요.", toastErrorSyle);
                                                        return;
                                                    }
                                                    open();
                                                }}
                                                className="bg-[#FF5463] text-white text-[14px] rounded-[5px] px-[12px] py-[7.5px]"
                                            >
                                                삭제
                                            </button>
                                        )}
                                        handleConfirm={deleteRow}
                                    />
                                </div>

                                <ConsoleTable
                                    thead={[
                                        {
                                            title: "NO",
                                            width: "8%",
                                            key: "index",
                                            content: (row: DataListProps, rowIndex) => {
                                                const index = data!.total_count - (searchForm.page - 1) * 10 - rowIndex;
                                                return (
                                                    <label
                                                        htmlFor={`list-${row.list_no}`}
                                                        className="flex items-center justify-center gap-x-1 px-2 flex-wrap"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            id={`list-${row.list_no}`}
                                                            checked={selectedRows.includes(row.list_no)}
                                                            onChange={() => toggleRow(row.list_no)}
                                                        />
                                                        <p className="break-all text-center">{makeIntComma(index) ?? 0}</p>
                                                    </label>
                                                );
                                            },
                                        },

                                        {
                                            title: "운송장번호",
                                            width: "12%",
                                            key: "bl_no",
                                            className: twMerge(theadStyle, "text-[#666666] px-2"),
                                        },
                                        {
                                            title: "제목",
                                            width: "25%",
                                            key: "subject",
                                            className: twMerge(theadStyle, "text-[#666666] px-2"),
                                            // content: (row: DataListProps, rowIndex) => (
                                            //     <Link to={`${row.list_no}`} className="hover:underline">
                                            //         {row.subject}
                                            //     </Link>
                                            // ),
                                        },
                                        {
                                            title: "요청업체명",
                                            width: "15%",
                                            key: "company",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                            content: (row: DataListProps) => (
                                                <div className="flex items-center justify-center gap-x-1">
                                                    <span>{row.company}</span>
                                                    {row.comment_count > 0 && (
                                                        <ConsoleBillCommentDialog
                                                            category={searchForm.category}
                                                            listNo={row.list_no}
                                                            count={row.comment_count}
                                                        />
                                                    )}
                                                </div>
                                            ),
                                        },
                                        {
                                            title: "연락드릴번호",
                                            width: "15%",
                                            key: "mobile",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                        },
                                        {
                                            title: "등록일",
                                            width: "15%",
                                            key: "w_date",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                        },
                                        {
                                            title: "상태",
                                            width: "10%",
                                            key: "comment",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                            content: (row: DataListProps, rowIndex) => {
                                                if (row.comment) {
                                                    return (
                                                        <Link to={`${row.list_no}`} className="hover:underline text-[#e61d27]">
                                                            답변보기
                                                        </Link>
                                                    );
                                                } else {
                                                    return <span className="text-[#0f8217]">답변대기</span>;
                                                }
                                            },
                                        },
                                    ]}
                                    tbody={data?.data_list || []}
                                    uniqueKeyName="list_no"
                                    isLoading={isLoading}
                                    trClassName="hover:bg-[#f8f8f8] cursor-pointer"
                                    trClick={row => navigate(`${row.list_no}`)}
                                />

                                <div className="flex justify-end mt-5">
                                    <button
                                        className="bg-black text-white rounded-[5px] px-4 py-2"
                                        onClick={() => navigate("create", { state: { page: searchParams.get("page") ?? "1" } })}
                                    >
                                        작성하기
                                    </button>
                                </div>
                            </div>

                            <Pagination
                                current={searchForm.page}
                                total={data?.total_count || 1}
                                pageSize={Number(searchForm.limit)}
                                onChange={newPage => {
                                    searchParams.set("page", String(newPage));
                                    setSearchParams(searchParams);
                                    // setSearchForm(prev => ({ ...prev, page: newPage }))
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
